import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Switch,
  Checkbox,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Layout from '../components/layout/Layout';
import PageTitle from '../components/PageTitle';
import { DEFAULT_DASHBOARD_CONFIG } from '../config/defaultDashboardConfig';
import {
  DASHBOARD_FUNCTION_KEYS,
  DASHBOARD_QUICK_ACTION_CATALOG,
} from '../config/dashboardQuickActionCatalog';
import {
  mergeDashboardConfig,
  canEditDashboardSettings,
  getOverviewCardDisplayOrder,
  applyQuickActionKeyChange,
  hasDuplicateQuickActionKeys,
  normalizeSalesOverviewTemplate,
} from '../utils/dashboardConfigUtils';
import { getDashboardConfig, saveDashboardConfig } from '../services/dashboardSettingsService';
import { DASHBOARD_COLORS } from '../components/dashboard/dashboardStyles';
import SalesOverviewTemplatePicker from '../components/dashboard/salesOverview/SalesOverviewTemplatePicker';

const OVERVIEW_LABELS = {
  bills: 'Total Bills',
  sales: 'Total Sales Amount',
  customers: 'Total Customers',
  pending: 'Total Pending Amount',
  lowStock: 'Low Stock Items',
  expenses: 'Total Expenses',
  suppliers: 'Total Suppliers',
  profit: 'Total Profit',
  creditCustomers: 'Total Credit Customers',
};

const SECTION_LABELS = {
  overview: 'Overview cards',
  quick_actions: 'Quick actions',
  alerts: 'Alerts & notifications',
  sales_overview: 'Sales overview',
  inventory_health: 'Inventory health',
  low_stock: 'Low stock items',
  best_sellers: 'Best selling products',
  pending_payments: 'Pending payments',
  recent_activity: 'Recent activities',
};

const sectionPaperSx = {
  p: { xs: 1.5, sm: 2 },
  border: `1px solid ${DASHBOARD_COLORS.border}`,
  borderRadius: 2,
  bgcolor: DASHBOARD_COLORS.surface,
  height: '100%',
};

const SettingsSection = ({ title, hint, children, noDivider }) => (
  <Paper elevation={0} sx={sectionPaperSx}>
    <Box sx={{ mb: hint ? 1 : 1.25 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.3 }}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.35 }}>
          {hint}
        </Typography>
      )}
    </Box>
    {!noDivider && <Divider sx={{ mb: 1.25, borderColor: DASHBOARD_COLORS.border }} />}
    {children}
  </Paper>
);

const compactRowSx = {
  py: 0.5,
  px: 1,
  minHeight: 36,
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  border: `1px solid ${DASHBOARD_COLORS.border}`,
  borderRadius: 1,
  bgcolor: '#fafbfc',
};

const SectionToggleRow = ({ label, checked, onToggle }) => (
  <Box sx={compactRowSx}>
    <Checkbox
      size="small"
      checked={checked}
      onChange={(e) => onToggle(e.target.checked)}
      sx={{ p: 0.5 }}
    />
    <Typography variant="body2" sx={{ fontSize: '0.8125rem', lineHeight: 1.3 }}>
      {label}
    </Typography>
  </Box>
);

const SortableRow = ({ id, label, checked, onToggle, order }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        ...compactRowSx,
        mb: 0.5,
        opacity: isDragging ? 0.85 : 1,
        zIndex: isDragging ? 1 : 'auto',
      }}
    >
      <Box
        sx={{
          minWidth: 22,
          height: 22,
          borderRadius: 1,
          bgcolor: checked ? '#eff6ff' : '#f3f4f6',
          color: checked ? '#2563eb' : 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.7rem',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {order}
      </Box>
      <Checkbox
        size="small"
        checked={checked}
        onChange={(e) => onToggle(e.target.checked)}
        onPointerDown={(e) => e.stopPropagation()}
        sx={{ p: 0.5 }}
      />
      <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8125rem', userSelect: 'none' }}>
        {label}
      </Typography>
      <Box
        component="button"
        type="button"
        aria-label={`Drag to reorder ${label}`}
        {...attributes}
        {...listeners}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: isDragging ? 'grabbing' : 'grab',
          color: 'text.secondary',
          touchAction: 'none',
          p: 0.25,
          border: 'none',
          background: 'transparent',
          borderRadius: 0.5,
          '&:focus-visible': { outline: `2px solid ${DASHBOARD_COLORS.accent}` },
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 18, pointerEvents: 'none' }} />
      </Box>
    </Box>
  );
};

const DashboardSettingsPage = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(() => mergeDashboardConfig(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (!canEditDashboardSettings()) {
      navigate('/dashboard');
      return;
    }
    getDashboardConfig({ useCache: false })
      .then(setConfig)
      .catch(() => setMessage('Could not load dashboard settings.'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    if (hasDuplicateQuickActionKeys(config.quickActions?.slots)) {
      setMessage('Each shortcut key (F1–F9) can only be used once. Please fix duplicate keys.');
      setSaving(false);
      return;
    }
    try {
      const enabledCount = (config.overviewCards?.enabled || []).length;
      const payload = {
        ...config,
        overviewCards: {
          ...config.overviewCards,
          maxVisible: Math.min(9, Math.max(enabledCount, config.overviewCards?.maxVisible ?? 8)),
        },
      };
      await saveDashboardConfig(payload);
      setMessage('Dashboard settings saved.');
    } catch {
      setMessage('Failed to save dashboard settings.');
    } finally {
      setSaving(false);
    }
  };

  const toggleOverviewCard = (cardId, enabled) => {
    setConfig((prev) => {
      const list = [...(prev.overviewCards?.enabled || [])];
      if (enabled && !list.includes(cardId)) list.push(cardId);
      if (!enabled) {
        const idx = list.indexOf(cardId);
        if (idx >= 0) list.splice(idx, 1);
      }
      return { ...prev, overviewCards: { ...prev.overviewCards, enabled: list } };
    });
  };

  const onOverviewDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setConfig((prev) => {
      const enabledSet = new Set(prev.overviewCards?.enabled || []);
      const currentOrder = getOverviewCardDisplayOrder(prev);
      const oldIndex = currentOrder.indexOf(active.id);
      const newIndex = currentOrder.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
      const newEnabled = newOrder.filter((id) => enabledSet.has(id));
      return {
        ...prev,
        overviewCards: { ...prev.overviewCards, enabled: newEnabled },
      };
    });
  };

  const toggleSection = (sectionId, visible) => {
    setConfig((prev) => ({
      ...prev,
      sections: (prev.sections || []).map((s) =>
        s.id === sectionId ? { ...s, visible } : s
      ),
    }));
  };

  const updateQuickActionSlot = (index, patch) => {
    setConfig((prev) => {
      let slots = [...(prev.quickActions?.slots || [])];
      if (patch.key) {
        slots = applyQuickActionKeyChange(slots, index, patch.key);
        const { key, ...rest } = patch;
        slots[index] = { ...slots[index], ...rest };
      } else {
        slots[index] = { ...slots[index], ...patch };
      }
      return { ...prev, quickActions: { ...prev.quickActions, slots } };
    });
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      </Layout>
    );
  }

  const enabledCards = config.overviewCards?.enabled || [];
  const allCardOrder = getOverviewCardDisplayOrder(config);

  return (
    <Layout>
      <PageTitle title="Dashboard Settings" />
      <Box sx={{ bgcolor: DASHBOARD_COLORS.bg, minHeight: '100%', pb: 10 }}>
        <Container maxWidth="lg" sx={{ pt: 1.5, px: { xs: 2, sm: 3 } }}>
          {/* Compact page header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
              mb: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/dashboard')}
                sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
              >
                Back
              </Button>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                  Customize dashboard
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Company-specific · not shared with other companies
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setConfig(mergeDashboardConfig(DEFAULT_DASHBOARD_CONFIG))}
              >
                Reset
              </Button>
            </Box>
          </Box>

          {message && (
            <Alert
              severity={message.includes('Failed') ? 'error' : 'success'}
              sx={{ py: 0.25, mb: 1.5, '& .MuiAlert-message': { py: 0.5 } }}
              onClose={() => setMessage(null)}
            >
              {message}
            </Alert>
          )}

          <Grid container spacing={1.5}>
            {/* Row 1: Overview cards (vertical) + Quick actions (vertical) */}
            <Grid item xs={12} md={6}>
              <SettingsSection
                title="Overview cards"
                hint="Numbers show order on dashboard. Drag to reorder."
                noDivider
              >
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={onOverviewDragEnd}
                >
                  <SortableContext items={allCardOrder} strategy={verticalListSortingStrategy}>
                    <Box>
                      {allCardOrder.map((cardId, index) => (
                        <SortableRow
                          key={cardId}
                          id={cardId}
                          order={index + 1}
                          label={OVERVIEW_LABELS[cardId] || cardId}
                          checked={enabledCards.includes(cardId)}
                          onToggle={(on) => toggleOverviewCard(cardId, on)}
                        />
                      ))}
                    </Box>
                  </SortableContext>
                </DndContext>
              </SettingsSection>
            </Grid>

            <Grid item xs={12} md={6}>
              <SettingsSection title="Quick actions" hint="Shortcuts F1–F9; duplicate keys swap on pick.">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    ...compactRowSx,
                    mb: 0.75,
                    bgcolor: DASHBOARD_COLORS.surface,
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                    Show quick actions row
                  </Typography>
                  <Switch
                    size="small"
                    checked={config.quickActions?.enabled !== false}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        quickActions: { ...prev.quickActions, enabled: e.target.checked },
                      }))
                    }
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {(config.quickActions?.slots || []).map((slot, index) => (
                    <Box
                      key={`${slot.id}-${index}`}
                      sx={{
                        ...compactRowSx,
                        display: 'grid',
                        gridTemplateColumns: '1fr 80px',
                        gap: 0.75,
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <Checkbox
                          size="small"
                          checked={slot.visible !== false}
                          onChange={(e) =>
                            updateQuickActionSlot(index, { visible: e.target.checked })
                          }
                          sx={{ p: 0.5 }}
                        />
                        <Typography variant="body2" noWrap sx={{ fontSize: '0.8125rem' }}>
                          {DASHBOARD_QUICK_ACTION_CATALOG[slot.id]?.label || slot.id}
                        </Typography>
                      </Box>
                      <FormControl size="small" sx={{ minWidth: 72 }}>
                        <InputLabel sx={{ fontSize: '0.75rem' }}>Key</InputLabel>
                        <Select
                          label="Key"
                          value={slot.key}
                          onChange={(e) => updateQuickActionSlot(index, { key: e.target.value })}
                          sx={{ fontSize: '0.8125rem', height: 32 }}
                        >
                          {DASHBOARD_FUNCTION_KEYS.map((k) => (
                            <MenuItem key={k} value={k} dense>
                              {k}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                </Box>
              </SettingsSection>
            </Grid>

            {/* Row 2: Dashboard sections + Alerts */}
            <Grid item xs={12} md={6}>
              <SettingsSection
                title="Dashboard sections"
                hint="Hidden sections free space for visible ones."
                noDivider
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {(config.sections || []).map((section) => (
                    <SectionToggleRow
                      key={section.id}
                      label={SECTION_LABELS[section.id] || section.id}
                      checked={section.visible !== false}
                      onToggle={(on) => toggleSection(section.id, on)}
                    />
                  ))}
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ ...compactRowSx, justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', pr: 1 }}>
                    Include out-of-stock in Low stock table
                  </Typography>
                  <Switch
                    size="small"
                    checked={config.lowStock?.includeOutOfStock !== false}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        lowStock: { includeOutOfStock: e.target.checked },
                      }))
                    }
                  />
                </Box>
              </SettingsSection>
            </Grid>

            <Grid item xs={12} md={6}>
              <SettingsSection
                title="Dashboard alerts"
                hint="These toggles control the alerts panel only (low stock, out of stock, credit balance, plan expiry)."
                noDivider
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {[
                        {
                          key: 'alertLow',
                          label: 'Low stock alert',
                          checked: config.alerts?.lowStock !== false,
                          onChange: (v) =>
                            setConfig((prev) => ({
                              ...prev,
                              alerts: { ...prev.alerts, lowStock: v },
                            })),
                        },
                        {
                          key: 'alertOos',
                          label: 'Out of stock alert',
                          checked: config.alerts?.outOfStock !== false,
                          onChange: (v) =>
                            setConfig((prev) => ({
                              ...prev,
                              alerts: { ...prev.alerts, outOfStock: v },
                            })),
                        },
                        {
                          key: 'alertCredit',
                          label: 'Credit balance alert',
                          checked: config.alerts?.creditDue !== false,
                          onChange: (v) =>
                            setConfig((prev) => ({
                              ...prev,
                              alerts: { ...prev.alerts, creditDue: v },
                            })),
                        },
                        {
                          key: 'alertSub',
                          label: 'Plan expiry alert',
                          checked: config.alerts?.subscription !== false,
                          onChange: (v) =>
                            setConfig((prev) => ({
                              ...prev,
                              alerts: { ...prev.alerts, subscription: v },
                            })),
                        },
                  ].map((row) => (
                    <Box key={row.key} sx={{ ...compactRowSx, justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem', pr: 1 }}>
                        {row.label}
                      </Typography>
                      <Switch
                        size="small"
                        checked={row.checked}
                        onChange={(e) => row.onChange(e.target.checked)}
                      />
                    </Box>
                  ))}
                </Box>
              </SettingsSection>
            </Grid>

            {/* Row 3: Sales overview templates */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={sectionPaperSx}>
                <SalesOverviewTemplatePicker
                  value={normalizeSalesOverviewTemplate(config.salesChart)}
                  onChange={(template) =>
                    setConfig((prev) => ({
                      ...prev,
                      salesChart: { ...prev.salesChart, template },
                    }))
                  }
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Layout>
  );
};

export default DashboardSettingsPage;
